// Community API Service
// Handles player profiles, teams, messages, and feed

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'API Error');
  }
  
  return response.json();
}

// Player Profile API
export const playerAPI = {
  // Get dropdown options
  getOptions: async () => {
    return fetchAPI('/api/players/options/all');
  },

  // Create player profile
  create: async (profileData) => {
    return fetchAPI('/api/players/json', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  },

  // Get all players
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchAPI(`/api/players?${query}`);
  },

  // Get single player
  get: async (playerId) => {
    return fetchAPI(`/api/players/${playerId}`);
  },

  // Update player profile
  update: async (playerId, profileData) => {
    return fetchAPI(`/api/players/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  // Verify password
  verifyPassword: async (playerId, password) => {
    return fetchAPI(`/api/players/${playerId}/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  // Delete profile
  delete: async (playerId, password) => {
    return fetchAPI(`/api/players/${playerId}`, {
      method: 'DELETE',
      body: JSON.stringify({ profile_password: password }),
    });
  },

  // Update privacy settings
  updatePrivacy: async (playerId, privacy, password) => {
    return fetchAPI(`/api/players/${playerId}/privacy`, {
      method: 'PUT',
      body: JSON.stringify({ privacy, profile_password: password }),
    });
  },
};

// Community Team API
export const communityTeamAPI = {
  // Create team
  create: async (teamData) => {
    return fetchAPI('/api/community-teams', {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  },

  // Get all teams
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchAPI(`/api/community-teams?${query}`);
  },

  // Get single team
  get: async (teamId) => {
    return fetchAPI(`/api/community-teams/${teamId}`);
  },

  // Update team
  update: async (teamId, teamData) => {
    return fetchAPI(`/api/community-teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
  },

  // Add member
  addMember: async (teamId, requesterId, playerId) => {
    return fetchAPI(`/api/community-teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ requester_player_id: requesterId, player_id: playerId }),
    });
  },

  // Remove member
  removeMember: async (teamId, requesterId, playerId) => {
    return fetchAPI(`/api/community-teams/${teamId}/members/${playerId}`, {
      method: 'DELETE',
      body: JSON.stringify({ requester_player_id: requesterId }),
    });
  },

  // Delete team
  delete: async (teamId, requesterId) => {
    return fetchAPI(`/api/community-teams/${teamId}`, {
      method: 'DELETE',
      body: JSON.stringify({ requester_player_id: requesterId }),
    });
  },

  // Get division options
  getDivisions: async () => {
    return fetchAPI('/api/community-teams/options/divisions');
  },
};

// Messaging API
export const messageAPI = {
  // Send message
  send: async (senderId, recipientId, recipientType, content) => {
    return fetchAPI('/api/messages', {
      method: 'POST',
      body: JSON.stringify({
        sender_id: senderId,
        recipient_id: recipientId,
        recipient_type: recipientType,
        content,
      }),
    });
  },

  // Get inbox
  getInbox: async (playerId, skip = 0, limit = 20) => {
    return fetchAPI(`/api/messages/inbox/${playerId}?skip=${skip}&limit=${limit}`);
  },

  // Get conversation
  getConversation: async (playerId, otherId, skip = 0, limit = 50) => {
    return fetchAPI(`/api/messages/conversation/${playerId}/${otherId}?skip=${skip}&limit=${limit}`);
  },

  // Get unread count
  getUnreadCount: async (playerId) => {
    return fetchAPI(`/api/messages/unread-count/${playerId}`);
  },

  // Mark as read
  markRead: async (messageId) => {
    return fetchAPI(`/api/messages/${messageId}/read`, { method: 'PUT' });
  },
};

// Feed API
export const feedAPI = {
  // Create post
  createPost: async (authorId, content, imageUrl = null, teamId = null) => {
    return fetchAPI('/api/feed/posts', {
      method: 'POST',
      body: JSON.stringify({
        author_id: authorId,
        content,
        image_url: imageUrl,
        team_id: teamId,
      }),
    });
  },

  // Get feed
  getPosts: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchAPI(`/api/feed/posts?${query}`);
  },

  // Get single post
  getPost: async (postId) => {
    return fetchAPI(`/api/feed/posts/${postId}`);
  },

  // Like/unlike post
  toggleLike: async (postId, playerId) => {
    return fetchAPI(`/api/feed/posts/${postId}/like`, {
      method: 'POST',
      body: JSON.stringify({ player_id: playerId }),
    });
  },

  // Add comment
  addComment: async (postId, authorId, content) => {
    return fetchAPI(`/api/feed/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ author_id: authorId, content }),
    });
  },

  // Get comments
  getComments: async (postId, skip = 0, limit = 50) => {
    return fetchAPI(`/api/feed/posts/${postId}/comments?skip=${skip}&limit=${limit}`);
  },

  // Delete post
  deletePost: async (postId, requesterId) => {
    return fetchAPI(`/api/feed/posts/${postId}`, {
      method: 'DELETE',
      body: JSON.stringify({ requester_id: requesterId }),
    });
  },

  // Delete comment
  deleteComment: async (postId, commentId, requesterId) => {
    return fetchAPI(`/api/feed/posts/${postId}/comments/${commentId}`, {
      method: 'DELETE',
      body: JSON.stringify({ requester_id: requesterId }),
    });
  },
};

export default {
  player: playerAPI,
  team: communityTeamAPI,
  message: messageAPI,
  feed: feedAPI,
};
